// components/NutritionCalculator.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Info, ChevronLeft } from 'lucide-react';
import { AuthBanner } from './AuthBanner';
import { Textarea } from "./ui/textarea";
import { useToast } from "./ui/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { SaveNoteDialog } from './SaveNoteDialog';
import { useNavigate } from 'react-router-dom';

const activityLevels = [
  { value: 1.2, label: "Sedentary", description: "You don't work out and have a desk job" },
  { value: 1.375, label: "Lightly Active", description: "You have a desk job, you work out 1-3x a week" },
  { value: 1.55, label: "Moderate", description: "You work out 3-5x a week OR you have a job with some manual labor" },
  { value: 1.725, label: "Heavy", description: "You work out 5-7x a week OR you have a job with a lot of manual labor" },
  { value: 1.9, label: "Very Heavy", description: "2-a-days, or a job with manual labor and frequent gym use" }
];

const NutritionCalculator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [userInfo, setUserInfo] = useState({
    age: 35,
    weight: 265,
    height: { feet: 6, inches: 4, cm: 193 },
    activityLevel: 1.375,
    gender: 'Male',
    bodyFat: 32,
    unit: 'imperial',
    goalType: 'deficit',            // Default goal
    deficitCalAmount: 500,
    surplusCalAmount: 500,
    // Add these default values
    proteinMultiplier: 1.0,        // Default protein multiplier
    fatMultiplier: 0.4,            // Default fat multiplier
  });

  const [results, setResults] = useState({
    bmr: 0,
    tdee: 0,
    deficitTDEE: 0,
    surplusTDEE: 0,
    macros: {
      protein: 0,
      carbs: 0,
      fat: 0
    },
    macroCalories: {
      proteinCalories: 0,
      carbCalories: 0,
      fatCalories: 0
    },
    leanWeight: 0
  });

  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveNote, setSaveNote] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [loading, setLoading] = useState(false);

  const SaveProgressDialog = useCallback(() => {
    return (
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Current Progress</DialogTitle>
            <DialogDescription>
              Add an optional note to track why you're saving this data point.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="note">Notes (optional)</Label>
              <Textarea
                id="note"
                placeholder="e.g., Starting new program, Post-vacation check-in..."
                value={saveNote}
                onChange={(e) => setSaveNote(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowSaveDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => saveNutritionHistory(saveNote)}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Progress"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }, [showSaveDialog, saveNote, isSaving]); // Add dependencies

  const saveNutritionHistory = async (note) => {
    if (!user) return;
    
    setIsSaving(true);
    try {
      console.log('Saving with results:', results); // Debug log
  
      const { error } = await supabase
        .from('nutrition_history')
        .insert({
          user_id: user.id,
          weight: userInfo.weight,
          body_fat: userInfo.bodyFat,
          bmr: results.bmr,
          tdee: results.tdee,
          target_calories: userInfo.goalType === 'deficit' 
            ? results.deficitTDEE 
            : userInfo.goalType === 'surplus'
              ? results.surplusTDEE
              : results.tdee,
          goal_type: userInfo.goalType,
          // Use the new unified macros structure
          protein_grams: results.macros.protein,
          carbs_grams: results.macros.carbs,
          fat_grams: results.macros.fat,
          protein_multiplier: userInfo.proteinMultiplier,
          fat_multiplier: userInfo.fatMultiplier,
          notes: note,
          activity_level: userInfo.activityLevel,
          height_cm: userInfo.unit === 'imperial' 
            ? (userInfo.height.feet * 30.48) + (userInfo.height.inches * 2.54)
            : userInfo.height.cm,
          age: userInfo.age
        });
  
      if (error) throw error;
  
      toast({
        title: "Progress Saved",
        description: "Your nutrition data has been saved successfully.",
      });
      
      setShowSaveDialog(false);
    } catch (error) {
      console.error('Error saving nutrition history:', error);
      toast({
        title: "Error Saving Progress",
        description: "There was a problem saving your data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const loadUserData = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
  
    try {
      // First, get the most recent nutrition history entry
      const { data: historyData, error: historyError } = await supabase
        .from('nutrition_history')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
  
      if (historyError && historyError.code !== 'PGRST116') {
        console.error('Error fetching history:', historyError);
        throw historyError;
      }
  
      // Then get user preferences
      const { data: prefsData, error: prefsError } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('id', user.id)
        .single();
  
      if (prefsError && prefsError.code !== 'PGRST116') {
        console.error('Error fetching preferences:', prefsError);
        throw prefsError;
      }
  
      // Update userInfo with the combined data, prioritizing history for weight and body fat
      setUserInfo(prevInfo => ({
        ...prevInfo,
        weight: historyData?.weight || prefsData?.weight || prevInfo.weight,
        bodyFat: historyData?.body_fat || prefsData?.body_fat || prevInfo.bodyFat,
        age: prefsData?.age || prevInfo.age,
        height: {
          feet: prefsData?.height_feet || prevInfo.height.feet,
          inches: prefsData?.height_inches || prevInfo.height.inches,
          cm: prefsData?.height_cm || prevInfo.height.cm
        },
        activityLevel: prefsData?.activity_level || prevInfo.activityLevel,
        gender: prefsData?.gender || prevInfo.gender,
        unit: prefsData?.unit || prevInfo.unit,
        goalType: prefsData?.goal_type || prevInfo.goalType,
        deficitCalAmount: prefsData?.deficit_cal_amount || prevInfo.deficitCalAmount,
        surplusCalAmount: prefsData?.surplus_cal_amount || prevInfo.surplusCalAmount,
        proteinMultiplier: prefsData?.protein_multiplier || prevInfo.proteinMultiplier,
        fatMultiplier: prefsData?.fat_multiplier || prevInfo.fatMultiplier
      }));
    } catch (error) {
      console.error('Error loading user data:', error);
      toast({
        title: "Error loading data",
        description: "There was a problem loading your data. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Load user preferences from Supabase
  useEffect(() => {
    loadUserData();
  }, [user]);

  // Save user preferences to Supabase
  const saveUserPreferences = async () => {
    if (!user) return;
    
    setIsSavingPreferences(true);
    try {
      // First save to user_preferences
      const dataToSave = {
        id: user.id,
        age: parseInt(userInfo.age),
        weight: parseFloat(userInfo.weight),
        height_feet: parseInt(userInfo.height.feet),
        height_inches: parseInt(userInfo.height.inches),
        height_cm: parseFloat(userInfo.height.cm),
        activity_level: parseFloat(userInfo.activityLevel),
        gender: userInfo.gender,
        body_fat: parseFloat(userInfo.bodyFat),
        unit: userInfo.unit,
        goal_type: userInfo.goalType,
        protein_multiplier: parseFloat(userInfo.proteinMultiplier),
        fat_multiplier: parseFloat(userInfo.fatMultiplier),
        deficit_cal_amount: parseInt(userInfo.deficitCalAmount),
        surplus_cal_amount: parseInt(userInfo.surplusCalAmount),
        updated_at: new Date().toISOString()
      };
  
      // Save to user_preferences
      const { error: prefsError } = await supabase
        .from('user_preferences')
        .upsert(dataToSave);
  
      if (prefsError) throw prefsError;
  
      // Then save to nutrition_history to maintain the source of truth
      const historyData = {
        user_id: user.id,
        weight: parseFloat(userInfo.weight),
        body_fat: parseFloat(userInfo.bodyFat),
        bmr: results.bmr,
        tdee: results.tdee,
        target_calories: userInfo.goalType === 'deficit' 
          ? results.deficitTDEE 
          : userInfo.goalType === 'surplus'
            ? results.surplusTDEE
            : results.tdee,
        goal_type: userInfo.goalType,
        protein_grams: results.macros.protein,
        carbs_grams: results.macros.carbs,
        fat_grams: results.macros.fat,
        protein_multiplier: userInfo.proteinMultiplier,
        fat_multiplier: userInfo.fatMultiplier,
        notes: "Updated from calculator settings",
        activity_level: userInfo.activityLevel,
        height_cm: userInfo.unit === 'imperial' 
          ? (userInfo.height.feet * 30.48) + (userInfo.height.inches * 2.54)
          : userInfo.height.cm,
        age: userInfo.age
      };
  
      const { error: historyError } = await supabase
        .from('nutrition_history')
        .insert([historyData]);
  
      if (historyError) throw historyError;
      
      console.log('Preferences and history saved successfully');
      setHasUnsavedChanges(false);
      
      toast({
        title: "Settings Saved",
        description: "Your personal information has been updated successfully.",
      });
    } catch (error) {
      console.error('Error saving data:', error);
      toast({
        title: "Error Saving Settings",
        description: "There was a problem saving your information. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSavingPreferences(false);
    }
  };

  useEffect(() => {
    calculateNutrition();
  }, [userInfo]);

  const calculateNutrition = () => {
    const { age, weight, height, activityLevel, gender, bodyFat, unit } = userInfo;
    const heightInCm = unit === 'imperial' ? (height.feet * 30.48) + (height.inches * 2.54) : height.cm;
    const weightInKg = unit === 'imperial' ? weight * 0.453592 : weight;
  
    // Calculate lean weight
    let leanWeightKg;
    if (bodyFat !== 0) {
      leanWeightKg = weightInKg * (1 - bodyFat / 100);
    } else {
      if (gender === 'Male') {
        leanWeightKg = (weightInKg * 0.407) + (heightInCm * 0.267) - 19.2;
      } else {
        leanWeightKg = weightInKg * 0.252;
      }
    }
  
    const leanWeightInLbs = unit === 'imperial' ? leanWeightKg / 0.453592 : leanWeightKg / 0.453592;
  
    // BMR Calculation
    let bmr;
    if (gender === 'Male') {
      bmr = (10 * leanWeightKg) + (6.25 * heightInCm) - (5 * age) + 5;
    } else {
      bmr = (10 * leanWeightKg) + (6.25 * heightInCm) - (5 * age) - 161;
    }
  
    const tdee = bmr * activityLevel;
    const deficitTDEE = tdee - userInfo.deficitCalAmount;
    const surplusTDEE = tdee + userInfo.surplusCalAmount;
  
    // Use the combined multipliers for protein and fat
    const proteinGrams = leanWeightInLbs * userInfo.proteinMultiplier;
    const fatGrams = leanWeightInLbs * userInfo.fatMultiplier;
  
    // Calculate calories from protein and fat
    const proteinCals = Math.round(proteinGrams * 4);
    const fatCals = Math.round(fatGrams * 9);
  
    // Calculate remaining calories for carbs based on goal
    let targetCalories, carbCals;
    if (userInfo.goalType === 'deficit') {
      targetCalories = deficitTDEE;
    } else if (userInfo.goalType === 'surplus') {
      targetCalories = surplusTDEE;
    } else {
      targetCalories = tdee;
    }
  
    carbCals = targetCalories - (proteinCals + fatCals);
    const carbGrams = carbCals > 0 ? (carbCals / 4) : 0;
  
    setResults({
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      deficitTDEE: Math.round(deficitTDEE),
      surplusTDEE: Math.round(surplusTDEE),
      macros: {
        protein: Math.round(proteinGrams),
        carbs: Math.round(carbGrams),
        fat: Math.round(fatGrams),
      },
      macroCalories: {
        proteinCalories: proteinCals,
        carbCalories: carbCals > 0 ? Math.round(carbCals) : 0,
        fatCalories: fatCals,
      },
      leanWeight: Math.round(unit === 'imperial' ? leanWeightKg / 0.453592 : leanWeightKg)
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const processedValue = e.target.type === 'number' ? parseFloat(value) || value : value;
    setUserInfo(prev => ({ ...prev, [name]: processedValue }));
    setHasUnsavedChanges(true);
  };

  const handleHeightChange = (type) => (e) => {
    setUserInfo(prev => ({
      ...prev,
      height: { ...prev.height, [type]: parseInt(e.target.value) }
    }));
    setHasUnsavedChanges(true);
  };

  const handleUnitToggle = async () => {
    const newUnit = userInfo.unit === 'imperial' ? 'metric' : 'imperial';
    
    const newUserInfo = {
      ...userInfo,
      unit: newUnit,
      height: newUnit === 'imperial' 
        ? {
            feet: Math.floor(userInfo.height.cm / 30.48),
            inches: Math.round((userInfo.height.cm % 30.48) / 2.54),
            cm: userInfo.height.cm
          }
        : {
            feet: userInfo.height.feet,
            inches: userInfo.height.inches,
            cm: Math.round(userInfo.height.feet * 30.48 + userInfo.height.inches * 2.54)
          },
      weight: newUnit === 'imperial' 
        ? Math.round(userInfo.weight / 0.453592) 
        : Math.round(userInfo.weight * 0.453592)
    };
  
    setUserInfo(newUserInfo);
  
    // Save to Supabase if user is authenticated
    if (user) {
      try {
        const { error } = await supabase
          .from('user_preferences')
          .upsert({
            id: user.id,
            unit: newUnit,
            weight: newUserInfo.weight,
            height_feet: newUserInfo.height.feet,
            height_inches: newUserInfo.height.inches,
            height_cm: newUserInfo.height.cm,
            updated_at: new Date().toISOString()
          });
  
        if (error) throw error;
      } catch (error) {
        console.error('Error saving unit preference:', error);
      }
    }
  };

  const renderMacroChart = (macros, macroCalories) => {
    // Add safety check
    if (!macros || !macroCalories) return null;
  
    const data = [
      { 
        name: 'Protein', 
        grams: Math.round(macros.protein || 0), 
        calories: Math.round(macroCalories.proteinCalories || 0) 
      },
      { 
        name: 'Carbs', 
        grams: Math.round(macros.carbs || 0), 
        calories: Math.round(macroCalories.carbCalories || 0) 
      },
      { 
        name: 'Fat', 
        grams: Math.round(macros.fat || 0), 
        calories: Math.round(macroCalories.fatCalories || 0) 
      }
    ];
  
    return (
      <>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
            <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
            <Tooltip />
            <Legend />
            <Bar yAxisId="left" dataKey="grams" fill="#8884d8" name="Grams" />
            <Bar yAxisId="right" dataKey="calories" fill="#82ca9d" name="Calories" />
          </BarChart>
        </ResponsiveContainer>
        <div className="grid grid-cols-3 gap-4 mt-4">
          {data.map((item) => (
            <div key={item.name} className="text-center">
              <p className="font-semibold">{item.name}</p>
              <p>{item.grams}g</p>
              <p>{item.calories} cal</p>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="container mx-auto p-4 max-w-4xl">
      <div className="flex justify-between items-center mb-4">
        <Button 
          variant="outline" 
          onClick={() => navigate('/')}
          className="flex items-center gap-2"
        >
          <ChevronLeft className="h-4 w-4" /> Back to Info
        </Button>
      </div>
  
      {!user && <AuthBanner />}
  
      <h1 className="text-3xl font-bold text-center mb-8">NutriNexus</h1>
      <div className="flex justify-center mb-4">
        <Button onClick={handleUnitToggle}>
          Toggle to {userInfo.unit === 'imperial' ? 'Metric' : 'Imperial'} Units
        </Button>
      </div>
      
      <Card className="mb-8 shadow-lg">
        <CardHeader>
          <CardTitle className="text-2xl">Personal Information</CardTitle>
          <CardDescription>Enter your details to calculate your nutritional needs</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="age">Age</Label>
              <Input id="age" type="number" name="age" value={userInfo.age} onChange={handleInputChange} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="weight">Weight ({userInfo.unit === 'imperial' ? 'lbs' : 'kg'})</Label>
              <Input id="weight" type="number" name="weight" value={userInfo.weight} onChange={handleInputChange} />
            </div>
            <div className="space-y-2">
              <Label>Height ({userInfo.unit === 'imperial' ? 'ft & in' : 'cm'})</Label>
              <div className="flex gap-2">
                {userInfo.unit === 'imperial' ? (
                  <>
                    <Input type="number" value={userInfo.height.feet} onChange={handleHeightChange('feet')} placeholder="Feet" />
                    <Input type="number" value={userInfo.height.inches} onChange={handleHeightChange('inches')} placeholder="Inches" />
                  </>
                ) : (
                  <Input type="number" value={userInfo.height.cm} onChange={handleHeightChange('cm')} placeholder="Centimeters" />
                )}
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="bodyFat">Body Fat %</Label>
                <Popover>
                  <PopoverTrigger>
                    <Info className="h-4 w-4 text-muted-foreground cursor-pointer" />
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="space-y-2">
                      <h4 className="font-medium">Estimating Body Fat Percentage</h4>
                      <p className="text-sm">If you don't have access to accurate measurements, you can estimate your body fat percentage using these methods:</p>
                      <ul className="text-sm list-disc pl-4 space-y-1">
                        <li>Visual comparison with reference photos</li>
                        <li>Navy Method: Measure neck and waist (and hips for women)</li>
                        <li>Use calipers for skin fold measurements</li>
                      </ul>
                      <p className="text-sm">For more accurate results, consider getting a DEXA scan or professional body fat measurement.</p>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <Input id="bodyFat" type="number" name="bodyFat" value={userInfo.bodyFat} onChange={handleInputChange} />
            </div>

            {/* Gender Selection */}
            <div className="space-y-2">
              <Label>Gender</Label>
              <RadioGroup
                name="gender"
                value={userInfo.gender}
                onValueChange={(value) => {
                  setUserInfo(prev => ({ ...prev, gender: value }));
                  setHasUnsavedChanges(true);
                }}
                className="flex space-x-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Male" id="male" />
                  <Label htmlFor="male">Male</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Female" id="female" />
                  <Label htmlFor="female">Female</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Goal Type Selection */}
            <div className="space-y-2">
              <Label>Goal Type</Label>
              <RadioGroup
                value={userInfo.goalType}
                onValueChange={(value) => {
                  setUserInfo(prev => ({ ...prev, goalType: value }));
                  setHasUnsavedChanges(true);
                }}
                className="flex flex-col space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="deficit" id="goal-deficit" />
                  <Label htmlFor="goal-deficit">Cut (Deficit)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="maintenance" id="goal-maintenance" />
                  <Label htmlFor="goal-maintenance">Maintenance</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="surplus" id="goal-surplus" />
                  <Label htmlFor="goal-surplus">Bulk (Surplus)</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Activity Level */}
            <div className="space-y-2 col-span-full">
              <Label>Activity Level</Label>
              <RadioGroup
                name="activityLevel"
                value={userInfo.activityLevel.toString()}
                onValueChange={(value) => {
                  setUserInfo(prev => ({ ...prev, activityLevel: parseFloat(value) }));
                  setHasUnsavedChanges(true);
                }}
                className="grid grid-cols-1 gap-4"
              >
                {activityLevels.map((level) => (
                  <div key={level.value} className="flex items-center space-x-2">
                    <RadioGroupItem value={level.value.toString()} id={`activity-${level.value}`} />
                    <Label htmlFor={`activity-${level.value}`} className="flex items-center cursor-pointer">
                      <span className="font-medium">{level.label}</span>
                      <Popover>
                        <PopoverTrigger>
                          <Info className="h-4 w-4 ml-2 text-gray-500" />
                        </PopoverTrigger>
                        <PopoverContent>{level.description}</PopoverContent>
                      </Popover>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Conditional Calorie Adjustment Sliders */}
            {userInfo.goalType === 'deficit' && (
              <div className="space-y-2 col-span-full">
                <div className="flex items-center space-x-2">
                  <Label>Deficit Calories: {userInfo.deficitCalAmount}</Label>
                  <Popover>
                    <PopoverTrigger>
                      <Info className="h-4 w-4 text-gray-500 cursor-pointer" />
                    </PopoverTrigger>
                    <PopoverContent>
                      Adjust the calorie deficit for cutting.
                    </PopoverContent>
                  </Popover>
                </div>
                <Slider
                  min={0}
                  max={1000}
                  step={50}
                  value={[userInfo.deficitCalAmount]}
                  onValueChange={(value) => {
                    setUserInfo(prev => ({ ...prev, deficitCalAmount: value[0] }));
                    setHasUnsavedChanges(true);
                  }}
                />
              {userInfo.deficitCalAmount < 250 && (
                <p className="text-red-500 text-sm mt-2">
                  Cutting calories by less than 250 (especially for longer periods) will slow your progress and can lead to diet fatigue, making it harder to stick to your goals. You should stick to 250-750 depending on your goals/health needs.
                </p>
              )}
              {userInfo.deficitCalAmount > 750 && (
                <p className="text-red-500 text-sm mt-2">
                  Cutting calories by more than 750 (especially for longer periods) can lead to diet fatigue, making it harder to stick to your goals. You should stick to 250-750 depending on your goals/health needs.
                </p>
              )}
              </div>
            )}

            {userInfo.goalType === 'surplus' && (
              <div className="space-y-2 col-span-full">
                <div className="flex items-center space-x-2">
                  <Label>Surplus Calories: {userInfo.surplusCalAmount}</Label>
                  <Popover>
                    <PopoverTrigger>
                      <Info className="h-4 w-4 text-gray-500 cursor-pointer" />
                    </PopoverTrigger>
                    <PopoverContent>
                      Adjust the calorie surplus for bulking.
                    </PopoverContent>
                  </Popover>
                </div>
                <Slider
                  min={0}
                  max={1000}
                  step={50}
                  value={[userInfo.surplusCalAmount]}
                  onValueChange={(value) => {
                    setUserInfo(prev => ({ ...prev, surplusCalAmount: value[0] }));
                    setHasUnsavedChanges(true);
                  }}
                />
              </div>
            )}

            {/* Combined Macro Sliders */}
            <div className="space-y-2 col-span-full">
              <Label>Protein (g/lb of lean mass): {userInfo.proteinMultiplier.toFixed(2)}</Label>
              <Slider
                min={0.6}
                max={1.2}
                step={0.05}
                value={[userInfo.proteinMultiplier]}
                onValueChange={(value) => {
                  setUserInfo(prev => ({ ...prev, proteinMultiplier: value[0] }));
                  setHasUnsavedChanges(true);
                }}
              />
            </div>

            <div className="space-y-2 col-span-full">
              <Label>Fat (g/lb of lean mass): {userInfo.fatMultiplier.toFixed(2)}</Label>
              <Slider
                min={0.3}
                max={0.75}
                step={0.05}
                value={[userInfo.fatMultiplier]}
                onValueChange={(value) => {
                  setUserInfo(prev => ({ ...prev, fatMultiplier: value[0] }));
                  setHasUnsavedChanges(true);
                }}
              />
            </div>
          </div>

          {/* Save Settings Button */}
          <div className="mt-6">
            <Button 
              onClick={saveUserPreferences}
              disabled={isSavingPreferences}
              className={`w-full ${hasUnsavedChanges ? 'bg-yellow-500 hover:bg-yellow-600' : ''}`}
            >
              {isSavingPreferences 
                ? "Saving Settings..." 
                : hasUnsavedChanges 
                  ? "Save Changes" 
                  : "Save Settings"}
            </Button>
          </div>
        </CardContent>
      </Card>
  
      {/* Results Card */}
      <Card className="shadow-lg bg-white dark:bg-gray-800">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-2xl text-black dark:text-white">Results</CardTitle>
            {user && (
              <Button 
                onClick={() => setShowSaveDialog(true)}
                className="ml-auto"
              >
                Save Progress
              </Button>
            )}
          </div>
          <CardDescription className="text-gray-600 dark:text-gray-300">
            Your calculated nutritional information
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Basic Results Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center justify-center gap-1 mb-1">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">BMR</p>
                <Popover>
                  <PopoverTrigger>
                    <Info className="h-4 w-4 text-muted-foreground cursor-pointer" />
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="space-y-2">
                      <h4 className="font-medium">Basal Metabolic Rate (BMR)</h4>
                      <p className="text-sm">The number of calories your body burns at complete rest to maintain basic life functions like:</p>
                      <ul className="text-sm list-disc pl-4">
                        <li>Breathing and circulation</li>
                        <li>Cell production and maintenance</li>
                        <li>Brain and nerve function</li>
                        <li>Temperature regulation</li>
                      </ul>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <p className="text-lg font-semibold text-black dark:text-white">{Math.round(results.bmr)} cal</p>
            </div>

            <div className="text-center p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center justify-center gap-1 mb-1">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">TDEE</p>
                <Popover>
                  <PopoverTrigger>
                    <Info className="h-4 w-4 text-muted-foreground cursor-pointer" />
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="space-y-2">
                      <h4 className="font-medium">Total Daily Energy Expenditure (TDEE)</h4>
                      <p className="text-sm">Your total daily caloric needs, including:</p>
                      <ul className="text-sm list-disc pl-4">
                        <li>Base metabolic rate (BMR)</li>
                        <li>Physical activity and exercise</li>
                        <li>Daily movement and tasks</li>
                        <li>Food digestion (thermic effect)</li>
                      </ul>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <p className="text-lg font-semibold text-black dark:text-white">{Math.round(results.tdee)} cal</p>
            </div>

            <div className="text-center p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center justify-center gap-1 mb-1">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Target Calories</p>
                <Popover>
                  <PopoverTrigger>
                    <Info className="h-4 w-4 text-muted-foreground cursor-pointer" />
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="space-y-2">
                      <h4 className="font-medium">Target Daily Calories</h4>
                      <p className="text-sm">Your personalized calorie target based on your goal:</p>
                      <ul className="text-sm list-disc pl-4">
                        <li>Cutting: TDEE minus deficit</li>
                        <li>Maintenance: Equal to TDEE</li>
                        <li>Bulking: TDEE plus surplus</li>
                      </ul>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <p className="text-lg font-semibold text-black dark:text-white">
                {userInfo.goalType === 'deficit' 
                  ? Math.round(results.deficitTDEE)
                  : userInfo.goalType === 'surplus'
                    ? Math.round(results.surplusTDEE)
                    : Math.round(results.tdee)} cal
              </p>
            </div>

            <div className="text-center p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center justify-center gap-1 mb-1">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Lean Weight</p>
                <Popover>
                  <PopoverTrigger>
                    <Info className="h-4 w-4 text-muted-foreground cursor-pointer" />
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="space-y-2">
                      <h4 className="font-medium">Lean Body Weight</h4>
                      <p className="text-sm">Your estimated weight excluding body fat, including:</p>
                      <ul className="text-sm list-disc pl-4">
                        <li>Muscle tissue</li>
                        <li>Bones and organs</li>
                        <li>Water weight</li>
                        <li>Essential fat (necessary for body function)</li>
                      </ul>
                      <p className="text-sm mt-2">Used to calculate protein and macro needs.</p>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <p className="text-lg font-semibold text-black dark:text-white">
                {Math.round(results.leanWeight)} {userInfo.unit === 'imperial' ? 'lbs' : 'kg'}
              </p>
            </div>
          </div>

          {/* Macros Chart */}
          <Tabs defaultValue="macros">
            <TabsList className="grid w-full grid-cols-1">
              <TabsTrigger value="macros">Macros</TabsTrigger>
            </TabsList>
            <TabsContent value="macros">
              {renderMacroChart(results.macros, results.macroCalories)}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      
      {/* Save Progress Dialog */}
      {user && (
        <SaveNoteDialog
          open={showSaveDialog}
          onOpenChange={setShowSaveDialog}
          onSave={saveNutritionHistory}
          isSaving={isSaving}
        />
      )}
    </div>
  );
}; // End of the component

export default NutritionCalculator; // Export statement outside the component
